/**
 * Classification Selector Component
 * PROJ-042 - Data Classification & Canvas Enhancement
 */

import { Lock, Shield } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDataClassifications } from '@/hooks/useDataClassifications';
import { Skeleton } from '@/components/ui/skeleton';

interface ClassificationSelectorProps {
  value: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
}

// Special value for "no classification" since Radix UI doesn't allow empty strings
const NO_CLASSIFICATION = '__none__';

export function ClassificationSelector({
  value,
  onChange,
  disabled
}: ClassificationSelectorProps) {
  const { classifications, loading } = useDataClassifications();
  const selected = classifications.find(c => c.code === value);

  if (loading) {
    return <Skeleton className="h-10 w-full" />;
  }

  // Convert null/empty to special value for Radix UI
  const selectValue = value || NO_CLASSIFICATION;

  // Handle change - convert special value back to null
  const handleChange = (v: string) => {
    onChange(v === NO_CLASSIFICATION ? null : v);
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Classification Level</Label>

      <Select
        value={selectValue}
        onValueChange={handleChange}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select classification...">
            {selected && (
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: selected.colorCode }}
                />
                <span>{selected.name}</span>
                <Badge variant="outline" className="ml-auto text-xs">
                  {selected.level}
                </Badge>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_CLASSIFICATION}>
            <span className="text-muted-foreground">No classification</span>
          </SelectItem>
          {classifications.map(c => (
            <SelectItem key={c.code} value={c.code}>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: c.colorCode }}
                />
                <span>{c.name}</span>
                <Badge variant="outline" className="ml-2 text-xs">
                  {c.level}
                </Badge>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Classification indicators */}
      {selected && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selected.requiresEncryption && (
            <Badge variant="outline" className="text-xs gap-1">
              <Lock className="h-3 w-3" />
              Encryption Required
            </Badge>
          )}
          {selected.requiresConsent && (
            <Badge variant="outline" className="text-xs gap-1">
              <Shield className="h-3 w-3" />
              Consent Required
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
